
import { useState } from 'react';

interface PackageInfo {
  type: string;
  weight: string;
  dimensions: {
    length: string;
    width: string;
    height: string;
  };
  description: string;
}

interface AddressInfo {
  departure: {
    address: string;
    landmark: string;
    contact: string;
  };
  arrival: {
    address: string;
    landmark: string;
    contact: string;
  };
}

interface PaymentInfo {
  method: string;
  details: any;
}

export default function GuestCheckoutPage() {
  const [currentStep, setCurrentStep] = useState(1);
  const [packageInfo, setPackageInfo] = useState<PackageInfo>({
    type: '',
    weight: '',
    dimensions: { length: '', width: '', height: '' },
    description: ''
  });
  const [addressInfo, setAddressInfo] = useState<AddressInfo>({
    departure: { address: '', landmark: '', contact: '' },
    arrival: { address: '', landmark: '', contact: '' }
  });
  const [paymentInfo, setPaymentInfo] = useState<PaymentInfo>({
    method: '',
    details: {}
  });
  const [estimatedPrice, setEstimatedPrice] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const packageTypes = [
    { id: 'document', name: 'Documents', icon: 'ri-file-text-line', basePrice: 2000 },
    { id: 'small', name: 'Petit colis', icon: 'ri-box-3-line', basePrice: 3500 },
    { id: 'medium', name: 'Colis moyen', icon: 'ri-archive-line', basePrice: 5000 },
    { id: 'large', name: 'Gros colis', icon: 'ri-inbox-archive-line', basePrice: 8000 },
    { id: 'fragile', name: 'Fragile', icon: 'ri-heart-pulse-line', basePrice: 6000 },
    { id: 'express', name: 'Express', icon: 'ri-rocket-line', basePrice: 4500 }
  ];

  const paymentMethods = [
    { id: 'mobile_money', name: 'Mobile Money', icon: 'ri-smartphone-line', fee: 0 },
    { id: 'card', name: 'Carte bancaire', icon: 'ri-bank-card-line', fee: 2 },
    { id: 'cash', name: 'Espèces à la livraison', icon: 'ri-money-dollar-circle-line', fee: 0 }
  ];

  const calculatePrice = () => {
    const selectedType = packageTypes.find(type => type.id === packageInfo.type);
    if (!selectedType) return 0;
    
    let price = selectedType.basePrice;
    const weight = parseFloat(packageInfo.weight) || 1;
    
    if (weight > 5) price += (weight - 5) * 500;
    if (packageInfo.type === 'express') price *= 1.5;
    if (packageInfo.type === 'fragile') price *= 1.3;
    
    return Math.round(price);
  };

  const handleNext = () => {
    if (currentStep === 1 && packageInfo.type && packageInfo.weight) {
      const price = calculatePrice();
      setEstimatedPrice(price);
      setCurrentStep(2);
    } else if (currentStep === 2 && addressInfo.departure.address && addressInfo.arrival.address) {
      setCurrentStep(3);
    } else if (currentStep === 3 && paymentInfo.method) {
      handleSubmitOrder();
    }
  };

  const handleSubmitOrder = async () => {
    setIsLoading(true);
    
    // Simulation de traitement de commande
    setTimeout(() => {
      setIsLoading(false);
      alert('Commande créée avec succès ! Vous recevrez un SMS avec le numéro de suivi.');
    }, 2000);
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-8">
      {[1, 2, 3].map((step) => (
        <div key={step} className="flex items-center">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${
            currentStep >= step ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600'
          }`}>
            {step}
          </div>
          {step < 3 && (
            <div className={`w-16 h-1 mx-2 ${
              currentStep > step ? 'bg-blue-600' : 'bg-gray-200'
            }`} />
          )}
        </div>
      ))}
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Informations sur le colis</h2>
        <p className="text-gray-600">Décrivez votre colis pour obtenir un devis précis</p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
        {packageTypes.map((type) => (
          <button
            key={type.id}
            onClick={() => setPackageInfo({...packageInfo, type: type.id})}
            className={`p-4 rounded-lg border-2 transition-all ${
              packageInfo.type === type.id
                ? 'border-blue-600 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <i className={`${type.icon} text-2xl mb-2 block ${
              packageInfo.type === type.id ? 'text-blue-600' : 'text-gray-600'
            }`}></i>
            <div className="text-sm font-medium">{type.name}</div>
            <div className="text-xs text-gray-500">{type.basePrice} FCFA</div>
          </button>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Poids approximatif (kg)
          </label>
          <input
            type="number"
            value={packageInfo.weight}
            onChange={(e) => setPackageInfo({...packageInfo, weight: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Ex: 2.5"
            min="0.1"
            step="0.1"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Description (optionnel)
          </label>
          <input
            type="text"
            value={packageInfo.description}
            onChange={(e) => setPackageInfo({...packageInfo, description: e.target.value})}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Ex: Vêtements, livres..."
          />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Longueur (cm)
          </label>
          <input
            type="number"
            value={packageInfo.dimensions.length}
            onChange={(e) => setPackageInfo({
              ...packageInfo,
              dimensions: {...packageInfo.dimensions, length: e.target.value}
            })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="30"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Largeur (cm)
          </label>
          <input
            type="number"
            value={packageInfo.dimensions.width}
            onChange={(e) => setPackageInfo({
              ...packageInfo,
              dimensions: {...packageInfo.dimensions, width: e.target.value}
            })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="20"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Hauteur (cm)
          </label>
          <input
            type="number"
            value={packageInfo.dimensions.height}
            onChange={(e) => setPackageInfo({
              ...packageInfo,
              dimensions: {...packageInfo.dimensions, height: e.target.value}
            })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="10"
          />
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Adresses de livraison</h2>
        <p className="text-gray-600">Indiquez les points de départ et d'arrivée</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Adresse de départ */}
        <div className="bg-green-50 p-6 rounded-lg border border-green-200">
          <h3 className="text-lg font-semibold text-green-800 mb-4 flex items-center">
            <i className="ri-map-pin-line mr-2"></i>
            Point de départ
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Adresse complète *
              </label>
              <textarea
                value={addressInfo.departure.address}
                onChange={(e) => setAddressInfo({
                  ...addressInfo,
                  departure: {...addressInfo.departure, address: e.target.value}
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                rows={3}
                placeholder="Ex: Rue 123, Quartier Plateau, Abidjan"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Point de repère
              </label>
              <input
                type="text"
                value={addressInfo.departure.landmark}
                onChange={(e) => setAddressInfo({
                  ...addressInfo,
                  departure: {...addressInfo.departure, landmark: e.target.value}
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Ex: Près de la pharmacie centrale"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contact expéditeur *
              </label>
              <input
                type="tel"
                value={addressInfo.departure.contact}
                onChange={(e) => setAddressInfo({
                  ...addressInfo,
                  departure: {...addressInfo.departure, contact: e.target.value}
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Ex: +225 07 12 34 56 78"
              />
            </div>
          </div>
        </div>

        {/* Adresse d'arrivée */}
        <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
          <h3 className="text-lg font-semibold text-blue-800 mb-4 flex items-center">
            <i className="ri-map-pin-2-line mr-2"></i>
            Point d'arrivée
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Adresse complète *
              </label>
              <textarea
                value={addressInfo.arrival.address}
                onChange={(e) => setAddressInfo({
                  ...addressInfo,
                  arrival: {...addressInfo.arrival, address: e.target.value}
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                rows={3}
                placeholder="Ex: Avenue 456, Quartier Cocody, Abidjan"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Point de repère
              </label>
              <input
                type="text"
                value={addressInfo.arrival.landmark}
                onChange={(e) => setAddressInfo({
                  ...addressInfo,
                  arrival: {...addressInfo.arrival, landmark: e.target.value}
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Ex: Face à l'école primaire"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Contact destinataire *
              </label>
              <input
                type="tel"
                value={addressInfo.arrival.contact}
                onChange={(e) => setAddressInfo({
                  ...addressInfo,
                  arrival: {...addressInfo.arrival, contact: e.target.value}
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Ex: +225 05 98 76 54 32"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Estimation du prix */}
      {estimatedPrice > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-lg font-semibold text-yellow-800">Prix estimé</h4>
              <p className="text-sm text-yellow-600">Basé sur les informations fournies</p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-yellow-800">{estimatedPrice.toLocaleString()} FCFA</div>
              <div className="text-sm text-yellow-600">Livraison sous 24h</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Paiement sécurisé</h2>
        <p className="text-gray-600">Choisissez votre mode de paiement préféré</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {paymentMethods.map((method) => (
          <button
            key={method.id}
            onClick={() => setPaymentInfo({...paymentInfo, method: method.id})}
            className={`p-6 rounded-lg border-2 transition-all ${
              paymentInfo.method === method.id
                ? 'border-blue-600 bg-blue-50'
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <i className={`${method.icon} text-3xl mb-3 block ${
              paymentInfo.method === method.id ? 'text-blue-600' : 'text-gray-600'
            }`}></i>
            <div className="text-lg font-medium mb-1">{method.name}</div>
            <div className="text-sm text-gray-500">
              {method.fee === 0 ? 'Sans frais' : `${method.fee}% de frais`}
            </div>
          </button>
        ))}
      </div>

      {/* Formulaire de paiement selon la méthode */}
      {paymentInfo.method === 'mobile_money' && (
        <div className="bg-green-50 p-6 rounded-lg border border-green-200">
          <h4 className="text-lg font-semibold text-green-800 mb-4">Mobile Money</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Opérateur
              </label>
              <select className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent">
                <option value="">Choisir un opérateur</option>
                <option value="orange">Orange Money</option>
                <option value="mtn">MTN Mobile Money</option>
                <option value="moov">Moov Money</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Numéro de téléphone
              </label>
              <input
                type="tel"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="+225 07 12 34 56 78"
              />
            </div>
          </div>
        </div>
      )}

      {paymentInfo.method === 'card' && (
        <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
          <h4 className="text-lg font-semibold text-blue-800 mb-4">Carte bancaire</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Numéro de carte
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="1234 5678 9012 3456"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Date d'expiration
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="MM/AA"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Code CVV
              </label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="123"
              />
            </div>
          </div>
        </div>
      )}

      {paymentInfo.method === 'cash' && (
        <div className="bg-orange-50 p-6 rounded-lg border border-orange-200">
          <h4 className="text-lg font-semibold text-orange-800 mb-4">Paiement à la livraison</h4>
          <div className="flex items-start space-x-3">
            <i className="ri-information-line text-orange-600 text-xl mt-1"></i>
            <div>
              <p className="text-orange-700 font-medium">Vous paierez en espèces lors de la livraison</p>
              <p className="text-orange-600 text-sm mt-1">
                Assurez-vous d'avoir le montant exact : {estimatedPrice.toLocaleString()} FCFA
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Récapitulatif de la commande */}
      <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
        <h4 className="text-lg font-semibold text-gray-800 mb-4">Récapitulatif de la commande</h4>
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-gray-600">Type de colis :</span>
            <span className="font-medium">
              {packageTypes.find(t => t.id === packageInfo.type)?.name}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Poids :</span>
            <span className="font-medium">{packageInfo.weight} kg</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Mode de paiement :</span>
            <span className="font-medium">
              {paymentMethods.find(m => m.id === paymentInfo.method)?.name}
            </span>
          </div>
          <div className="border-t pt-3 flex justify-between text-lg font-bold">
            <span>Total à payer :</span>
            <span className="text-blue-600">{estimatedPrice.toLocaleString()} FCFA</span>
          </div>
        </div>
      </div>

      {/* Notifications */}
      <div className="bg-blue-50 p-6 rounded-lg border border-blue-200">
        <h4 className="text-lg font-semibold text-blue-800 mb-4 flex items-center">
          <i className="ri-notification-3-line mr-2"></i>
          Notifications de suivi
        </h4>
        <div className="space-y-3">
          <label className="flex items-center">
            <input type="checkbox" defaultChecked className="mr-3 text-blue-600" />
            <span className="text-blue-700">Recevoir des SMS de suivi</span>
          </label>
          <label className="flex items-center">
            <input type="checkbox" defaultChecked className="mr-3 text-blue-600" />
            <span className="text-blue-700">Recevoir des emails de confirmation</span>
          </label>
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email pour les notifications (optionnel)
            </label>
            <input
              type="email"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="votre@email.com"
            />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <div className="text-2xl font-bold text-blue-600" style={{fontFamily: '"Pacifico", serif'}}>
                ONAPILA
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <button className="text-gray-600 hover:text-gray-900 px-3 py-2 rounded-md text-sm font-medium">
                Aide
              </button>
              <button className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-blue-700 whitespace-nowrap">
                Créer un compte
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-bold mb-4">Commander sans inscription</h1>
          <p className="text-xl text-blue-100 mb-8">
            Envoyez vos colis rapidement et en toute sécurité, sans créer de compte
          </p>
          <div className="flex items-center justify-center space-x-8 text-sm">
            <div className="flex items-center">
              <i className="ri-shield-check-line text-2xl mr-2"></i>
              <span>Paiement sécurisé</span>
            </div>
            <div className="flex items-center">
              <i className="ri-time-line text-2xl mr-2"></i>
              <span>Livraison rapide</span>
            </div>
            <div className="flex items-center">
              <i className="ri-customer-service-2-line text-2xl mr-2"></i>
              <span>Support 24/7</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-xl shadow-lg p-8">
          {renderStepIndicator()}
          
          {currentStep === 1 && renderStep1()}
          {currentStep === 2 && renderStep2()}
          {currentStep === 3 && renderStep3()}

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-8 pt-6 border-t">
            <button
              onClick={() => setCurrentStep(Math.max(1, currentStep - 1))}
              disabled={currentStep === 1}
              className={`px-6 py-3 rounded-lg font-medium whitespace-nowrap ${
                currentStep === 1
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              <i className="ri-arrow-left-line mr-2"></i>
              Précédent
            </button>

            <button
              onClick={handleNext}
              disabled={
                (currentStep === 1 && (!packageInfo.type || !packageInfo.weight)) ||
                (currentStep === 2 && (!addressInfo.departure.address || !addressInfo.arrival.address)) ||
                (currentStep === 3 && !paymentInfo.method) ||
                isLoading
              }
              className={`px-6 py-3 rounded-lg font-medium whitespace-nowrap ${
                ((currentStep === 1 && (!packageInfo.type || !packageInfo.weight)) ||
                (currentStep === 2 && (!addressInfo.departure.address || !addressInfo.arrival.address)) ||
                (currentStep === 3 && !paymentInfo.method) ||
                isLoading)
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {isLoading ? (
                <>
                  <i className="ri-loader-4-line animate-spin mr-2"></i>
                  Traitement...
                </>
              ) : currentStep === 3 ? (
                <>
                  Confirmer la commande
                  <i className="ri-check-line ml-2"></i>
                </>
              ) : (
                <>
                  Suivant
                  <i className="ri-arrow-right-line ml-2"></i>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Avantages */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white p-6 rounded-lg shadow-md text-center">
            <i className="ri-user-add-line text-3xl text-green-600 mb-4"></i>
            <h3 className="text-lg font-semibold mb-2">Sans inscription</h3>
            <p className="text-gray-600 text-sm">
              Commandez immédiatement sans créer de compte
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md text-center">
            <i className="ri-heart-line text-3xl text-blue-600 mb-4"></i>
            <h3 className="text-lg font-semibold mb-2">Expérience simplifiée</h3>
            <p className="text-gray-600 text-sm">
              Interface intuitive pour une commande rapide
            </p>
          </div>
          <div className="bg-white p-6 rounded-lg shadow-md text-center">
            <i className="ri-star-line text-3xl text-yellow-600 mb-4"></i>
            <h3 className="text-lg font-semibold mb-2">Conversion facile</h3>
            <p className="text-gray-600 text-sm">
              Créez un compte après votre première expérience positive
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <div className="text-2xl font-bold mb-4" style={{fontFamily: '"Pacifico", serif'}}>
                ONAPILA
              </div>
              <p className="text-gray-400 text-sm">
                Votre partenaire de confiance pour tous vos besoins de livraison en Côte d'Ivoire.
              </p>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="#" className="hover:text-white">Livraison express</a></li>
                <li><a href="#" className="hover:text-white">Livraison standard</a></li>
                <li><a href="#" className="hover:text-white">Colis fragiles</a></li>
                <li><a href="#" className="hover:text-white">Documents</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Support</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="#" className="hover:text-white">Centre d'aide</a></li>
                <li><a href="#" className="hover:text-white">Contact</a></li>
                <li><a href="#" className="hover:text-white">Suivi de colis</a></li>
                <li><a href="#" className="hover:text-white">Réclamations</a></li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-semibold mb-4">Contact</h4>
              <div className="space-y-2 text-sm text-gray-400">
                <div className="flex items-center">
                  <i className="ri-phone-line mr-2"></i>
                  +225 27 20 30 40 50
                </div>
                <div className="flex items-center">
                  <i className="ri-mail-line mr-2"></i>
                  support@onapila.ci
                </div>
                <div className="flex items-center">
                  <i className="ri-map-pin-line mr-2"></i>
                  Abidjan, Côte d'Ivoire
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm text-gray-400">
            <p>&copy; 2024 ONAPILA. Tous droits réservés. | 
              <a href="https://readdy.ai/?origin=logo" className="hover:text-white ml-1">Powered by Readdy</a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
